type TransitionKind = 'iris' | 'sunrise'

interface ViewTransition {
  finished: Promise<void>
  ready?: Promise<void>
  updateCallbackDone?: Promise<void>
}

interface ViewTransitionDocument {
  startViewTransition?: (update: () => void | Promise<unknown>) => ViewTransition
}

/** True when the user asked for less motion; jsdom and old engines have no matchMedia. */
export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Runs a DOM-changing update as a circular reveal (theme.css: bl-iris)
 * growing from `origin`, a viewport point. The browser captures the old view,
 * runs `update` (sync, or a promise that settles once the new view is in the
 * DOM) and reveals the new one. Without the View Transitions API, or with
 * reduced motion, the update simply happens. Settles once the update has run.
 */
export function revealFrom(kind: TransitionKind, origin: { x: number; y: number } | null, update: () => void | Promise<unknown>): Promise<void> {
  const doc = document as Document & ViewTransitionDocument
  if (!doc.startViewTransition || prefersReducedMotion()) {
    return Promise.resolve(update()).then(() => undefined)
  }
  const root = document.documentElement
  const x = origin?.x ?? window.innerWidth / 2
  const y = origin?.y ?? window.innerHeight / 2
  const radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y))
  root.style.setProperty('--bl-vt-x', `${x}px`)
  root.style.setProperty('--bl-vt-y', `${y}px`)
  root.style.setProperty('--bl-vt-r', `${Math.ceil(radius)}px`)
  root.dataset.transition = kind
  const transition = doc.startViewTransition(update)
  // A transition the browser skips rejects these; the update still ran.
  transition.ready?.catch(() => {})
  transition.updateCallbackDone?.catch(() => {})
  void transition.finished.catch(() => {}).finally(() => {
    if (root.dataset.transition === kind) delete root.dataset.transition
  })
  return (transition.updateCallbackDone ?? transition.finished).catch(() => undefined)
}

/** The centre of an element, for revealFrom(). */
export function centreOf(element: Element | null): { x: number; y: number } | null {
  if (!element) return null
  const rect = element.getBoundingClientRect()
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 }
}

let shellWaiters: Array<() => void> = []

/** Settles once the dashboard shell has committed (DashboardLayout), or after `timeout` ms. */
export function shellReady(timeout = 1200): Promise<void> {
  return new Promise(resolve => {
    const done = () => {
      window.clearTimeout(timer)
      resolve()
    }
    const timer = window.setTimeout(() => {
      shellWaiters = shellWaiters.filter(waiter => waiter !== done)
      resolve()
    }, timeout)
    shellWaiters.push(done)
  })
}

/** Called by the dashboard shell when it first commits. */
export function markShellReady(): void {
  const waiters = shellWaiters
  shellWaiters = []
  for (const waiter of waiters) waiter()
}
